import { useDashboardFilters } from '../../utils/user/dashboardTimeRange';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import DateTimeFilterBar from '../../components/dateTimeFilterBar';
import Header from '../../components/header';
import MapCluster from '../../components/mapCluster';
import ReloadButton from '../../components/reloadButton';
import Timezone from '../../components/timezone';
import DropdownSearch from '../../components/dropdownSearch';
import colors from '../../assets/colors';
import { setAlert } from '../../redux/dashboard/actions';
import { getPortals, getSelectedPortal } from '../../redux/portal/actions';
import API from '../../services/api';
import utils from '../../utils';
import calendar from '../../utils/calendar';
import { getWidgetOptions } from '../../utils/widgets';
import { getWebPropertiesFromResponse, isVisibleWebEmbed } from '../../containers/features/webEmbedUtils';
import { metricCards } from './metrics';
import { storeTemplate, isStoreTemplate, isAttributionTemplateAvailable } from './storeTemplate';
import StoreAttributionDashboard from './StoreAttributionDashboard';
import Loading from '../../components/loading';

import './styles.scss';

const chartConfig = {
  colors: [colors.mainPallet[0], colors.mainPallet[3]],
  height: 260
};

const widgetInfo = {
  'exposed-devices': 'Unique mobile sessions exposed during the selected period.',
  'website-visits': 'Distinct non-bot website sessions during the selected period.',
  'conversion-rate': 'Percentage of distinct matched MAIDs relative to distinct exposed MAIDs.',
  'exposed-devices-vs-website-visits': 'Comparison of exposed mobile sessions and website visits over time.',
  'top-landing-pages': 'Website sessions ranked by the first page visited.',
  'visits-by-sites': 'Distinct matched MAIDs grouped by attributed site.',
  'visits-by-day': 'Website sessions grouped by day.'
};

const getInfo = id => JSON.stringify({ text: widgetInfo[id] });

const getRequestPeriods = ({ startDate, endDate }, timeZone) => {
  const start = new Date(startDate);
  const selectedEnd = new Date(endDate);
  const end = new Date(selectedEnd);
  end.setSeconds(end.getSeconds() + 1, 0);
  const duration = end.getTime() - start.getTime();
  const previousStart = new Date(start.getTime() - duration);

  return {
    current: { startTime: start.toISOString(), endTime: end.toISOString(), timeZone },
    previous: { startTime: previousStart.toISOString(), endTime: start.toISOString(), timeZone }
  };
};

const getDailyWebsiteVisits = (response, timeZone) => {
  const websiteVisits = response?.timeSeries?.series?.find(series => series.key === 'website_visits');
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    timeZone
  });
  const points = (websiteVisits?.points || []).map(point => ({
    label: dateFormatter.format(new Date(point.timestamp)),
    value: Number(point.value)
  }));

  return {
    categories: points.map(point => point.label),
    data: [{ label: websiteVisits?.label || 'Website Visits', value: points }]
  };
};

const getExposedDevicesVsWebsiteVisits = (response, timeZone) => {
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    timeZone
  });
  const series = response?.timeSeries?.series || [];
  const categories = series[0]?.points?.map(point => dateFormatter.format(new Date(point.timestamp))) || [];

  return {
    categories,
    data: series.map(item => ({
      label: item.label,
      value: (item.points || []).map(point => ({
        label: dateFormatter.format(new Date(point.timestamp)),
        value: Number(point.value)
      }))
    }))
  };
};

const getScalarMetric = (metricId, response, previousResponse) => {
  const metric = metricCards.find(card => card.id === metricId);
  const label = metric?.title || metricId;

  return {
    ...metric,
    data: [{ label, value: Number(response?.scalar?.value || 0) }],
    previous: previousResponse ? [{ label, value: Number(previousResponse?.scalar?.value || 0) }] : undefined
  };
};

const getTopSites = response =>
  (response?.categorySeries?.points || [])
    // The API denotes an unresolved site with both coordinates set to zero.
    .filter(point => Number(point.latitude) !== 0 || Number(point.longitude) !== 0)
    .map(point => ({
      id: point.key,
      displayName: point.label || point.key,
      latitude: point.latitude,
      longitude: point.longitude,
      maids: Number(point.value || 0)
    }));

const getTopLandingPages = response =>
  (response?.categorySeries?.points || []).map(point => ({
    label: point.label || point.key,
    value: Number(point.value || 0)
  }));

const getTemplateWidgetIds = template =>
  (template?.rows || []).reduce(
    (widgetIds, row) => [...widgetIds, ...(row.widgets || []).map(widget => widget.id)],
    []
  );

const useInnerWidgetProperty = (hostRef, selector, property, value) => {
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let frameId;

    const applyProperty = () => {
      const element = host.querySelector(selector);
      if (!element) return false;
      // widget-container renders its inner display asynchronously after data
      // changes. Apply the value in the next frame so that render does not
      // overwrite it with its internal default.
      frameId = window.requestAnimationFrame(() => {
        element[property] = value;
      });
      return true;
    };

    if (applyProperty()) {
      return () => window.cancelAnimationFrame(frameId);
    }

    const observer = new MutationObserver(() => {
      if (applyProperty()) observer.disconnect();
    });
    observer.observe(host, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [hostRef, property, selector, value]);
};

const useRawBarTooltip = (hostRef, enabled) => {
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !enabled) return undefined;

    const setRawTooltip = chart => {
      if (chart?.update) {
        chart.update({ tooltip: { pointFormat: '<b>{point.y}</b>' } });
      }
    };
    const handleChartChange = event => setRawTooltip(event.detail);

    host.addEventListener('chartChange', handleChartChange);
    setRawTooltip(host.querySelector('display-bar')?.chart);
    return () => host.removeEventListener('chartChange', handleChartChange);
  }, [enabled, hostRef]);
};

const DigitWidget = ({ metric, loading }) => {
  const widgetRef = useRef(null);
  useInnerWidgetProperty(widgetRef, 'display-digit', 'previous', metric.previous);

  return (
    <widget-container
      ref={widgetRef}
      id={metric.id}
      widget-title={metric.title}
      display="digit"
      data={JSON.stringify(metric.data)}
      config={JSON.stringify({})}
      format={metric.format}
      unit={metric.unit}
      is-loading={loading}
      info={getInfo(metric.id)}
      options={getWidgetOptions({ display: 'digit' })}
    />
  );
};

const TopLandingPages = ({ data, loading }) => {
  return (
    <widget-container
      className="web-attribution-list"
      id="top-landing-pages"
      widget-title="Top Landing Pages"
      display="stats-list"
      data={JSON.stringify(data)}
      config={JSON.stringify({ height: 270 })}
      plot-options={JSON.stringify({ displayBars: true })}
      is-loading={loading}
      info={getInfo('top-landing-pages')}
      options={getWidgetOptions({ display: 'stats-list' })}
    />
  );
};

const WebAnalyticsWidget = ({ id, title, display, data, config, plotOptions, loading, rawBarTooltip }) => {
  const widgetRef = useRef(null);
  useRawBarTooltip(widgetRef, rawBarTooltip);

  return (
    <widget-container
      ref={widgetRef}
      id={id}
      widget-title={title}
      display={display}
      data={JSON.stringify(data)}
      config={JSON.stringify(config || {})}
      plot-options={plotOptions ? JSON.stringify(plotOptions) : undefined}
      is-loading={loading}
      info={getInfo(id)}
      options={getWidgetOptions({ display })}
    />
  );
};

const FeatureWebAnalyticsScreen = () => {
  const [lastUpdated, setLastUpdated] = useState(new Date().toISOString());
  const [webPropertyIds, setWebPropertyIds] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [exposedDevicesMetric, setExposedDevicesMetric] = useState(null);
  const [websiteVisitsMetric, setWebsiteVisitsMetric] = useState(null);
  const [conversionRateMetric, setConversionRateMetric] = useState(null);
  const [exposedDevicesVsWebsiteVisits, setExposedDevicesVsWebsiteVisits] = useState({
    categories: [],
    data: []
  });
  const [topLandingPages, setTopLandingPages] = useState([]);
  const [topSites, setTopSites] = useState([]);
  const [dailyVisits, setDailyVisits] = useState({ categories: [], data: [] });
  const [appliedFilters, setAppliedFilters] = useDashboardFilters({
    startDate: calendar.getDefaultDate('start'),
    endDate: calendar.getDefaultDate('end'),
    filterByTime: {},
    filterByWeekday: []
  });
  const reportTimezone = appliedFilters.isTimeRangeLocked && appliedFilters.timezone
    ? appliedFilters.timezone : timezone;
  const dispatch = useDispatch();
  const portals = useSelector(getPortals);
  const portal = useSelector(getSelectedPortal);
  const availableTemplates = useMemo(() => templates.filter(template =>
    isAttributionTemplateAvailable(template, portal?.activeFeatures)), [templates, portal?.activeFeatures]);
  const activeTemplate = availableTemplates.find(template => template.id === selectedTemplate?.id) ||
    availableTemplates[0] || null;
  const selectedWeekdays = appliedFilters.filterByWeekday || [];
  const weekdayFilterKey = selectedWeekdays.join(',');
  const timeOfDayStart = appliedFilters.filterByTime?.start || '';
  const timeOfDayEnd = appliedFilters.filterByTime?.end || '';

  const showError = useCallback(
    message => dispatch(setAlert(utils.generateAlert(message, 'error'))),
    [dispatch]
  );

  useEffect(() => {
    let mounted = true;

    API.getTemplates('', "collection = 'ATTRIBUTION' AND enabled = true")
      .then(response => {
        if (!mounted) return;
        const attributionTemplates = [...(response?.templates || [])];
        if (!attributionTemplates.some(isStoreTemplate)) attributionTemplates.push(storeTemplate);
        setTemplates(attributionTemplates);
        setSelectedTemplate(attributionTemplates[0] || null);
      })
      .catch(error => {
        console.error('Error loading Attribution templates:', error);
        if (mounted) {
          setTemplates([storeTemplate]);
          setSelectedTemplate(storeTemplate);
          showError('Failed to load Web Analytics templates.');
        }
      });

    return () => {
      mounted = false;
    };
  }, [showError]);

  useEffect(() => {
    if (!portal?.id || isStoreTemplate(activeTemplate) ||
      !portal.activeFeatures?.includes('WEB_ANALYTICS')) return undefined;
    let mounted = true;

    setWebPropertyIds(null);
    setAnalyticsLoading(true);
    API.getWebProperties(portal.id)
      .then(webPropertyResponse => {
        if (!mounted) return;
        const embedIds = getWebPropertiesFromResponse(webPropertyResponse)
          .filter(isVisibleWebEmbed)
          .map(webProperty => webProperty?.id)
          .filter(Boolean);

        setWebPropertyIds(Array.from(new Set(embedIds)));
      })
      .catch(error => {
        console.error('Error loading Web Properties:', error);
        if (mounted) {
          setAnalyticsLoading(false);
          showError('Failed to load Web Embeds.');
        }
      });

    return () => {
      mounted = false;
    };
  }, [portal?.id, portal?.activeFeatures, activeTemplate?.id, showError]);

  useEffect(() => {
    if (!portal?.id || !portal.activeFeatures?.includes('WEB_ANALYTICS') ||
      webPropertyIds === null || isStoreTemplate(activeTemplate)) return undefined;
    let mounted = true;
    const periods = getRequestPeriods(appliedFilters, reportTimezone);
    // Website analytics views only accept web-property selection; date and timezone
    // are sent in the period, while weekday/time-of-day filters are not supported.
    const request = period => ({
      scope: { brandPortalId: portal.id },
      period,
      selection: { webPropertyIds }
    });
    const exposedDevicesRequest = period => ({
      scope: { brandPortalId: portal.id },
      period
    });

    setAnalyticsLoading(true);
    Promise.all([
      API.getAnalyticsViewData('exposed_devices', exposedDevicesRequest(periods.current)),
      API.getAnalyticsViewData('exposed_devices', exposedDevicesRequest(periods.previous)),
      API.getAnalyticsViewData('website_visits', request(periods.current)),
      API.getAnalyticsViewData('website_visits', request(periods.previous)),
      API.getAnalyticsViewData('exposed_signals_vs_website_visits', request(periods.current)),
      API.getAnalyticsViewData('website_top_landing_pages', request(periods.current)),
      API.getAnalyticsViewData('website_ip_match_conversion_rate', request(periods.current)),
      API.getAnalyticsViewData('website_ip_match_top_sites', request(periods.current))
    ])
      .then(([
        currentExposedDevices,
        previousExposedDevices,
        currentVisits,
        previousVisits,
        visitsOverTime,
        topLandingPagesResponse,
        currentConversionRate,
        topSitesResponse
      ]) => {
        if (!mounted) return;
        setExposedDevicesMetric(
          getScalarMetric('exposed-devices', currentExposedDevices, previousExposedDevices)
        );
        setWebsiteVisitsMetric(getScalarMetric('website-visits', currentVisits, previousVisits));
        setExposedDevicesVsWebsiteVisits(getExposedDevicesVsWebsiteVisits(visitsOverTime, reportTimezone));
        setTopLandingPages(getTopLandingPages(topLandingPagesResponse));
        setConversionRateMetric(
          getScalarMetric('conversion-rate', currentConversionRate)
        );
        setDailyVisits(getDailyWebsiteVisits(visitsOverTime, reportTimezone));
        setTopSites(getTopSites(topSitesResponse));
      })
      .catch(error => {
        console.error('Error loading Web Analytics:', error);
        if (mounted) showError('Failed to load Web Analytics data.');
      })
      .finally(() => {
        if (mounted) setAnalyticsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [
    appliedFilters.endDate,
    appliedFilters.startDate,
    lastUpdated,
    portal?.id,
    showError,
    timeOfDayEnd,
    timeOfDayStart,
    reportTimezone,
    weekdayFilterKey,
    webPropertyIds,
    activeTemplate?.id
  ]);

  const refreshAction = useMemo(
    () => isStoreTemplate(activeTemplate) ? [] : [
      {
        type: 'reactComponent',
        value: (
          <ReloadButton
            key="reload-btn"
            contentName="Web attribution data"
            lastUpdatedCB={setLastUpdated}
          />
        )
      }
    ],
    [activeTemplate?.id]
  );

  useEffect(() => {
    if (availableTemplates.length && !availableTemplates.some(template => template.id === selectedTemplate?.id)) {
      setSelectedTemplate(availableTemplates[0]);
    }
  }, [availableTemplates, selectedTemplate?.id]);

  const selectedTemplateWidgetIds = useMemo(() => new Set(getTemplateWidgetIds(activeTemplate)), [activeTemplate]);
  const hasTemplateLayout = Array.isArray(activeTemplate?.rows);
  const isWidgetVisible = widgetId => !hasTemplateLayout || selectedTemplateWidgetIds.has(widgetId);
  const visibleMetricCards = metricCards.filter(metric => isWidgetVisible(metric.id));

  const onPeriodSelected = nextFilters => {
    setAppliedFilters(current => ({ ...current, ...nextFilters }));
  };

  if (portals === null) {
    return (
      <div className="d-flex flex-fill justify-content-center align-items-center">
        <Loading size={8} />
      </div>
    );
  }

  if (!portal?.id) {
    return <Redirect to="/charts" />;
  }

  return (
    <div className="web-attribution-screen d-flex flex-column flex-fill">
      <Header
        title="Attribution Charts"
        icon={isStoreTemplate(activeTemplate) ? 'store' : 'webAnalytics'}
        filters={
          <DropdownSearch
            label="Template"
            options={availableTemplates.map(template => ({ label: template.displayName || template.id, value: template }))}
            selected={
              activeTemplate
                ? { label: activeTemplate.displayName || activeTemplate.id, value: activeTemplate }
                : null
            }
            onSelect={setSelectedTemplate}
            disabled={availableTemplates.length === 0}
          />
        }
        actions={refreshAction}
      />
      {isStoreTemplate(activeTemplate) ? (
        <StoreAttributionDashboard portal={portal} lastUpdated={lastUpdated} template={activeTemplate} />
      ) : <main id="web-attribution-container" data-last-updated={lastUpdated}>
        <div className="filter-bar">
          <div className="filters">
            <DateTimeFilterBar
              appliedFilters={appliedFilters}
              onPeriodSelected={onPeriodSelected}
              hideIntervalSelector
            />
            <div className="preference-control">
              <Timezone label="Timezone" leftAlign onSelect={setTimezone}
                selected={{ value: reportTimezone, label: reportTimezone }}
                disabled={Boolean(appliedFilters.isTimeRangeLocked && appliedFilters.timezone)} />
            </div>
          </div>
        </div>

        <div className="scrollable-container web-attribution-scroll">
          <div id="web-attribution-chart-container">
            {visibleMetricCards.length > 0 && (
              <section className="chart-row web-attribution-metrics" aria-label="Web attribution summary">
                {visibleMetricCards.map(metric => {
                  const isExposedDevices = metric.id === 'exposed-devices';
                  const isWebsiteVisits = metric.id === 'website-visits';
                  const isConversionRate = metric.id === 'conversion-rate';
                  const displayedMetric = isExposedDevices
                    ? exposedDevicesMetric || { ...metric, data: [], previous: [] }
                    : isWebsiteVisits
                    ? websiteVisitsMetric || { ...metric, data: [], previous: [] }
                    : isConversionRate
                      ? conversionRateMetric || { ...metric, data: [], previous: [] }
                      : metric;
                  return (
                    <DigitWidget
                      key={metric.id}
                      metric={displayedMetric}
                      loading={(isExposedDevices || isWebsiteVisits || isConversionRate) && analyticsLoading}
                    />
                  );
                })}
              </section>
            )}

            {isWidgetVisible('exposed-devices-vs-website-visits') && (
              <div className="chart-row">
                <WebAnalyticsWidget
                  id="exposed-devices-vs-website-visits"
                  title="Exposed Devices vs. Website Visits"
                  display="line"
                  data={exposedDevicesVsWebsiteVisits.data}
                  config={chartConfig}
                  plotOptions={{ categories: exposedDevicesVsWebsiteVisits.categories }}
                  loading={analyticsLoading}
                />
              </div>
            )}

            {(isWidgetVisible('top-landing-pages') || isWidgetVisible('visits-by-sites')) && (
              <div className="chart-row web-attribution-split-row">
                {isWidgetVisible('top-landing-pages') && (
                  <TopLandingPages data={topLandingPages} loading={analyticsLoading} />
                )}

                {isWidgetVisible('visits-by-sites') && (
                  <div className="card web-attribution-map">
                    <div className="card-header">
                      <h5>Visits by Sites</h5>
                      <div className="widget-action-buttons">
                        <info-box
                          host-id="visits-by-sites"
                          widget-title="Visits by Sites"
                          text={widgetInfo['visits-by-sites']}
                        />
                      </div>
                    </div>
                    <div className="card-body">
                      <MapCluster sites={topSites} metricLabel="Matched MAIDs" metricKey="maids" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {isWidgetVisible('visits-by-day') && (
              <div className="chart-row">
                <WebAnalyticsWidget
                  id="visits-by-day"
                  title="Visits by Day"
                  display="bar"
                  data={dailyVisits.data}
                  config={{ colors: [colors.mainPallet[0]], height: 245 }}
                  plotOptions={{
                    categories: dailyVisits.categories,
                    singleDataserie: true
                  }}
                  loading={analyticsLoading}
                  rawBarTooltip
                />
              </div>
            )}
          </div>
        </div>
      </main>}
    </div>
  );
};

export default FeatureWebAnalyticsScreen;
